/* SAP PDP Sticky Buy-Box Controller.
 *
 * Implements "scroll-through-then-pin" sticky behaviour for the PDP right
 * column, matching Tula's bottom-aligned pin pattern exactly.
 *
 * Strategy:
 *   Aurora already sets  position:sticky; top:4rem  on .product__info-wrapper.
 *   That top-pins the wrapper at 40px, which hides the bottom of a tall box
 *   (1700-2200px) inside a 900px viewport.  We replace Aurora's top with a
 *   computed value so the BOTTOM of the wrapper aligns with the viewport bottom
 *   minus a gap, letting the page scroll through the full box before it pins.
 *
 *   For a tall box:   top = viewportH - BOTTOM_GAP - boxHeight  < 0  (negative)
 *                     => bottom-aligned pin, scroll-through visible.
 *   For a short box:  top = headerGap  (positive)
 *                     => simple top-pin under the header (no clipping).
 *
 *   An inline style.top wins over Aurora's CSS top:4rem without !important.
 *
 * Desktop-only gate:
 *   We apply the logic ONLY when the wrapper is actually sticky (Aurora
 *   scopes position:sticky to >=768px desktop layout).  Checking
 *   getComputedStyle(wrapper).position === 'sticky' is the most reliable gate.
 *   On mobile (767px and below) Aurora sets position:relative so the gate
 *   is false and we clear any inline top, restoring normal flow.
 *
 * Timing note:
 *   Aurora's JS runs at DOMContentLoaded and applies the sticky position class
 *   a few frames later.  A deferred script that reads position at
 *   DOMContentLoaded may still see 'relative'.  We use requestAnimationFrame
 *   inside a retry loop to wait until position resolves to 'sticky' before
 *   setting the top.  The loop bails after ~500ms to avoid running forever
 *   on non-PDP pages where sticky is never applied.
 *
 * Recomputation triggers:
 *   - window 'resize' (passive) -- viewport changes, zoom, orientation
 *   - ResizeObserver on the wrapper -- accordion open/close changes box height
 *   - 'shopify:section:load' -- theme editor re-renders
 *
 * Inline JS in section files is forbidden (SAP rule). All JS lives here.
 */
(function () {
  'use strict';

  var BOTTOM_GAP = 32;   // px gap from viewport bottom when box is pinned (Tula ~39px)
  var HEADER_EXTRA = 24; // px breathing room below the sticky header
  var MAX_RETRY_MS = 600; // bail out of the sticky-wait loop after this many ms

  var resizeObserver = null;

  function computeAndSetTop(wrapper) {
    if (!wrapper) return;

    var cs = getComputedStyle(wrapper);

    // Gate: only apply when Aurora has resolved position:sticky.
    // On mobile Aurora sets position:relative -- clear inline top.
    if (cs.position !== 'sticky') {
      wrapper.style.top = '';
      return;
    }

    var boxHeight = wrapper.offsetHeight;
    var viewportH = window.innerHeight;

    // Header gap: read the runtime CSS var Aurora exposes on <body>.
    // Falls back to 0 if not set (e.g. theme editor context).
    var rawHeaderH = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--gsc-header-height')
    );
    var headerH = isNaN(rawHeaderH) ? 0 : rawHeaderH;
    var headerGap = headerH + HEADER_EXTRA;

    // Scroll-through-then-pin: use the smaller (more negative) of the two.
    // Tall box => negative top => bottom of box at (viewportH - BOTTOM_GAP).
    // Short box => positive headerGap => top-pin just below the header.
    var top = Math.min(headerGap, viewportH - BOTTOM_GAP - boxHeight);

    wrapper.style.top = top + 'px';
  }

  /* waitThenPin: poll via rAF until position:sticky resolves (Aurora's JS
   * may apply the class a few frames after DOMContentLoaded), then set top
   * and wire the persistent listeners. Bails after MAX_RETRY_MS. */
  function waitThenPin(wrapper) {
    var started = Date.now();

    function attempt() {
      // If Aurora still hasn't applied sticky, retry next frame.
      if (getComputedStyle(wrapper).position !== 'sticky') {
        if (Date.now() - started < MAX_RETRY_MS) {
          requestAnimationFrame(attempt);
        }
        // Bail: not a sticky desktop layout -- do nothing.
        return;
      }

      // Position is sticky -- compute and set.
      computeAndSetTop(wrapper);
    }

    requestAnimationFrame(attempt);
  }

  function initStickyBuybox() {
    var wrapper = document.querySelector('.product__info-wrapper');
    if (!wrapper) return;

    // Persistent listeners attach here, NOT in waitThenPin's success path:
    // a portrait load (<768px, position:relative) exhausts the retry budget
    // without pinning, and a later portrait->landscape rotation must still
    // re-arm — otherwise the wrapper pins with Aurora's stock top:4rem and
    // clips the buy box (the exact bug this controller exists to fix).
    // computeAndSetTop self-gates: it clears the inline top when not sticky.

    // Resize listener: recompute on viewport change, incl. rotation.
    window.addEventListener('resize', function () {
      computeAndSetTop(wrapper);
    }, { passive: true });

    // ResizeObserver: recompute when box height changes (accordion open/close).
    if (typeof ResizeObserver !== 'undefined') {
      if (resizeObserver) resizeObserver.disconnect();
      resizeObserver = new ResizeObserver(function () {
        computeAndSetTop(wrapper);
      });
      resizeObserver.observe(wrapper);
    }

    waitThenPin(wrapper);
  }

  // Bootstrap: init on DOMContentLoaded (or immediately if already loaded).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyBuybox);
  } else {
    initStickyBuybox();
  }

  // Re-init on Shopify theme-editor section re-renders.
  document.addEventListener('shopify:section:load', function () {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    initStickyBuybox();
  });
})();
